/** Eksportene under «common» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/common/`. */
import type { ExportMeta } from '../types.js';

/**
 * KONTAKTLISTA: tabellen på Kontakter, med samme filter (`ListContactsQuery` uten side), samme
 * sortering og de samme kolonnene som brukeren har valgt der (`tableKey: 'contacts'`).
 */
const CONTACTS: ExportMeta = {
  id: 'common.contacts',
  domain: 'common',
  kind: 'table',
  titleKey: 'export.common.contacts.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['contact:list'],
  guest: false,
  tableKey: 'contacts',
  columns: [
    { key: 'displayName', labelKey: 'export.common.contacts.columns.displayName', type: 'text' },
    { key: 'jobTitle', labelKey: 'export.common.contacts.columns.jobTitle', type: 'text' },
    {
      key: 'organizations',
      labelKey: 'export.common.contacts.columns.organizations',
      type: 'text',
    },
    { key: 'email', labelKey: 'export.common.contacts.columns.email', type: 'email' },
    { key: 'phone', labelKey: 'export.common.contacts.columns.phone', type: 'phone' },
    { key: 'tags', labelKey: 'export.common.contacts.columns.tags', type: 'text' },
    {
      key: 'companyName',
      labelKey: 'export.common.contacts.columns.companyName',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'notes',
      labelKey: 'export.common.contacts.columns.notes',
      type: 'multiline',
      defaultHidden: true,
    },
    {
      key: 'preferredLocale',
      labelKey: 'export.common.contacts.columns.preferredLocale',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'createdAt',
      labelKey: 'export.common.contacts.columns.createdAt',
      type: 'date',
      defaultHidden: true,
    },
    {
      key: 'updatedAt',
      labelKey: 'export.common.contacts.columns.updatedAt',
      type: 'date',
      defaultHidden: true,
    },
  ],
};

/**
 * EKSTERNE ORGANISASJONER: Kontakter › Eksterne organisasjoner, med søket og sorteringen fanen
 * spør med (`ListContactCompaniesQuery` uten side), merkede rader og tabellens kolonner.
 */
const EXTERNAL_ORGANIZATIONS: ExportMeta = {
  id: 'common.external-organizations',
  domain: 'common',
  kind: 'table',
  titleKey: 'export.common.externalOrganizations.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['contact:list'],
  guest: false,
  tableKey: 'contact-companies',
  columns: [
    { key: 'name', labelKey: 'export.common.externalOrganizations.columns.name', type: 'text' },
    {
      key: 'contactCount',
      labelKey: 'export.common.externalOrganizations.columns.contactCount',
      type: 'integer',
    },
    {
      key: 'organizationNumber',
      labelKey: 'export.common.externalOrganizations.columns.organizationNumber',
      type: 'postcode',
    },
    { key: 'email', labelKey: 'export.common.externalOrganizations.columns.email', type: 'email' },
    { key: 'phone', labelKey: 'export.common.externalOrganizations.columns.phone', type: 'phone' },
    {
      key: 'websiteUrl',
      labelKey: 'export.common.externalOrganizations.columns.websiteUrl',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'address',
      labelKey: 'export.common.externalOrganizations.columns.address',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'notes',
      labelKey: 'export.common.externalOrganizations.columns.notes',
      type: 'multiline',
      defaultHidden: true,
    },
    {
      key: 'link',
      labelKey: 'export.common.externalOrganizations.columns.link',
      type: 'status',
      defaultHidden: true,
    },
    {
      key: 'createdAt',
      labelKey: 'export.common.externalOrganizations.columns.createdAt',
      type: 'date',
      defaultHidden: true,
    },
    {
      key: 'updatedAt',
      labelKey: 'export.common.externalOrganizations.columns.updatedAt',
      type: 'date',
      defaultHidden: true,
    },
  ],
};

/**
 * EN TRÅD I INNBOKS SOM DOKUMENT: meldingene i rekkefølge med avsender og tid, deltakerne og
 * vedleggene som liste. Bare det leseren ser i tråden: `message:list` er deltakernes for alle roller.
 */
const THREAD: ExportMeta = {
  id: 'common.thread',
  domain: 'common',
  kind: 'document',
  titleKey: 'export.common.thread.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object'],
  requires: ['thread:list', 'message:list'],
  guest: false,
  sections: [
    { key: 'messages', labelKey: 'export.common.thread.sections.messages' },
    { key: 'participants', labelKey: 'export.common.thread.sections.participants' },
    { key: 'attachments', labelKey: 'export.common.thread.sections.attachments' },
  ],
};

/**
 * DOKUMENTLISTA I EN MAPPE: det filutforskeren viser — mappene først, så filene — i den åpne mappa
 * (eller treffene i mappa og undermappene under et søk), med de samme kolonnene (`tableKey: 'documents'`).
 */
const DOCUMENTS: ExportMeta = {
  id: 'common.documents',
  domain: 'common',
  kind: 'table',
  titleKey: 'export.common.documents.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['document:list'],
  guest: false,
  tableKey: 'documents',
  columns: [
    { key: 'icon', labelKey: 'export.common.documents.columns.kind', type: 'text' },
    { key: 'name', labelKey: 'export.common.documents.columns.name', type: 'text' },
    { key: 'updatedAt', labelKey: 'export.common.documents.columns.updatedAt', type: 'datetime' },
    { key: 'updatedBy', labelKey: 'export.common.documents.columns.updatedBy', type: 'text' },
    { key: 'size', labelKey: 'export.common.documents.columns.size', type: 'text' },
    {
      key: 'advanceSection',
      labelKey: 'export.common.documents.columns.advanceSection',
      type: 'status',
      defaultHidden: true,
    },
    {
      key: 'department',
      labelKey: 'export.common.documents.columns.department',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'fileType',
      labelKey: 'export.common.documents.columns.fileType',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'location',
      labelKey: 'export.common.documents.columns.location',
      type: 'text',
      defaultHidden: true,
    },
    {
      key: 'fileCount',
      labelKey: 'export.common.documents.columns.fileCount',
      type: 'integer',
      defaultHidden: true,
    },
    {
      key: 'subfolderCount',
      labelKey: 'export.common.documents.columns.subfolderCount',
      type: 'integer',
      defaultHidden: true,
    },
    {
      key: 'scanStatus',
      labelKey: 'export.common.documents.columns.scanStatus',
      type: 'status',
      defaultHidden: true,
    },
    {
      key: 'scannedAt',
      labelKey: 'export.common.documents.columns.scannedAt',
      type: 'datetime',
      defaultHidden: true,
    },
  ],
};

export const COMMON_EXPORTS: readonly ExportMeta[] = [
  CONTACTS,
  EXTERNAL_ORGANIZATIONS,
  THREAD,
  DOCUMENTS,
];
